import { useTranslation } from 'react-i18next';

import { CheckCircleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData } from '@remix-run/react';

import type { loader as communityLoader } from '~/routes/community';

import type { ActionMenuItem } from '../_components/ui/ActionMenu';

import { useCommunityMemberActions } from '../_context/CommunityMemberActionsProvider';

interface UseMemberModerationOptions {
  memberId: string;
  memberName: string;
  canBanAuthor: boolean;
  canUnbanAuthor: boolean;
  canBlockAuthor?: boolean;
  canUnblockAuthor?: boolean;
}

/**
 * Builds the member-moderation menu items for a post/comment author. The dialogs and
 * mutations live in `CommunityMemberActionsProvider` (single owner); this hook just wires
 * the menu items to the provider's actions, so it can be called once per card/comment
 * without re-creating fetchers or modals.
 */
export const useMemberModeration = ({
  memberId,
  memberName,
  canBanAuthor,
  canUnbanAuthor,
  canBlockAuthor = false,
  canUnblockAuthor = false,
}: UseMemberModerationOptions): { menuItems: ActionMenuItem[] } => {
  const { t } = useTranslation();
  const actions = useCommunityMemberActions();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const memberBlockingEnabled = Boolean(layoutData?.community?.member_blocking_enabled);
  const member = { id: memberId, name: memberName };

  const menuItems: ActionMenuItem[] = [];

  if (canBanAuthor) {
    menuItems.push({
      key: 'ban_member',
      label: t('community.action_menu.ban_member'),
      icon: NoSymbolIcon,
      onClick: () => actions.openBanDialog(member),
      variant: 'danger',
    });
  }
  if (canUnbanAuthor) {
    menuItems.push({
      key: 'unban_member',
      label: t('community.action_menu.unban_member'),
      icon: CheckCircleIcon,
      onClick: () => actions.openUnbanDialog(member),
    });
  }
  if (canBlockAuthor && memberBlockingEnabled) {
    menuItems.push({
      key: 'block_member',
      label: t('community.action_menu.block_member'),
      icon: NoSymbolIcon,
      onClick: () => actions.openBlockDialog(member),
      variant: 'danger',
    });
  }
  if (canUnblockAuthor) {
    menuItems.push({
      key: 'unblock_member',
      label: t('community.action_menu.unblock_member'),
      icon: CheckCircleIcon,
      onClick: () => actions.openUnblockDialog(member),
    });
  }

  return { menuItems };
};
